import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { apiFetch, qk } from '@/lib/queries/client';
import { useCamadaDetalle } from '@/lib/queries/camadas';
import { fmtFecha, fmtInt } from '@/lib/format';
import type { RegistroDiario } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';
import { useEntityId } from '@/lib/offline/operations';

type ParteConNave = RegistroDiario & { nave_codigo: string };
type ParteDiario = {
  fecha: string;
  naves: string[];
  bajas_muerte: number;
  bajas_triaje: number;
  agua_litros: number | null;
};

/** Listado completo de partes de todos los lotes de una camada.
 * Los partes pertenecen al lote/nave; esta pantalla los agrupa para que
 * el ganadero los consulte desde el detalle de la camada. */
export function CamadaPartesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { camadaId: camadaIdParam } = useParams<{ camadaId: string }>();
  const camadaId = useEntityId(camadaIdParam, 'camadas');
  const ownerId = useAuthStore((s) => s.user?.id ?? null);
  const detalleQ = useCamadaDetalle(camadaId);
  const lotes = useMemo(() => detalleQ.data?.lotes ?? [], [detalleQ.data?.lotes]);

  const partesQ = useQueries({
    queries: lotes.map((lote) => ({
      queryKey: [...qk('registros-diarios'), 'lote', lote.lote_id, '', 'owner', ownerId],
      networkMode: 'always' as const,
      queryFn: () => apiFetch<RegistroDiario[]>(`/v1/lotes-nave/${lote.lote_id}/registros-diarios`),
      enabled: lote.lote_id !== 0,
      staleTime: 30 * 1000,
    })),
  });

  const partes = useMemo<ParteConNave[]>(() => {
    return lotes
      .flatMap((lote, index) =>
        (partesQ[index]?.data ?? []).map((parte) => ({ ...parte, nave_codigo: lote.nave_codigo })),
      )
      .sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id);
  }, [lotes, partesQ]);

  const partesDiarios = useMemo<ParteDiario[]>(() => {
    const porFecha = new Map<string, ParteConNave[]>();
    for (const parte of partes) {
      const registros = porFecha.get(parte.fecha) ?? [];
      registros.push(parte);
      porFecha.set(parte.fecha, registros);
    }
    return Array.from(porFecha, ([fecha, registros]) => ({
      fecha,
      naves: Array.from(new Set(registros.map((parte) => parte.nave_codigo))).sort((a, b) =>
        a.localeCompare(b),
      ),
      bajas_muerte: registros.reduce((total, parte) => total + parte.bajas_muerte, 0),
      bajas_triaje: registros.reduce((total, parte) => total + parte.bajas_triaje, 0),
      agua_litros: registros.every((parte) => parte.agua_litros != null)
        ? registros.reduce((total, parte) => total + (parte.agua_litros ?? 0), 0)
        : null,
    })).sort((a, b) => b.fecha.localeCompare(a.fecha));
  }, [partes]);

  const cargando = detalleQ.isLoading || partesQ.some((query) => query.isLoading);
  const camada = detalleQ.data?.camada;

  if (cargando) {
    return (
      <main className="container mx-auto max-w-md px-4 py-6">
        <p className="text-text-secondary">{t('common.loading')}</p>
      </main>
    );
  }

  if (detalleQ.isError || !camada) {
    return (
      <main className="container mx-auto max-w-md px-4 py-6">
        <p role="alert" className="text-warning-text">
          {t('common.error')}
        </p>
        <button
          type="button"
          onClick={() => navigate('/camadas')}
          className="mt-3 text-sm text-accent"
        >
          ← {t('camada_detalle.volver')}
        </button>
      </main>
    );
  }

  return (
    <main className="container mx-auto max-w-md px-4 pb-8 pt-4">
      <header className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(`/camadas/${camada.id}`)}
          className="rounded-full p-2 text-text-secondary hover:bg-background"
          aria-label={t('camada_detalle.volver')}
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </button>
        <div>
          <h1 className="text-xl font-bold text-text-primary">
            {t('camada_detalle.partes_listado_titulo')}
          </h1>
          <p className="text-sm text-text-secondary">
            {camada.codigo_lote} ·{' '}
            {t('camada_detalle.partes_listado_subtitulo', { n: partesDiarios.length })}
          </p>
        </div>
      </header>

      {partesDiarios.length === 0 ? (
        <p className="rounded-lg border border-border bg-surface p-3 text-sm text-text-secondary">
          {t('camada_detalle.partes_vacio')}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="camada-listado-partes">
          {partesDiarios.map((parte) => {
            const contenido = (
              <>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-text-primary">
                    {fmtFecha(parte.fecha)}
                    <span className="ml-2 text-[13px] font-normal text-text-secondary">
                      · {parte.naves.join(' · ')}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-text-secondary">
                    {t('camada_detalle.parte_bajas', {
                      muerte: parte.bajas_muerte,
                      triaje: parte.bajas_triaje,
                    })}
                    {parte.agua_litros != null &&
                      ` · ${t('camada_detalle.parte_agua', { agua: fmtInt(parte.agua_litros) })}`}
                  </p>
                </div>
                {!camada.cerrada && (
                  <ChevronRight className="h-5 w-5 shrink-0 text-text-muted" aria-hidden />
                )}
              </>
            );

            return (
              <li key={parte.fecha}>
                {camada.cerrada ? (
                  <div className="flex items-center gap-3 rounded-lg border border-border bg-surface p-3">
                    {contenido}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/camadas/${camadaId}/registro-diario?fecha=${parte.fecha}`)
                    }
                    className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface p-3 text-left hover:border-accent"
                  >
                    {contenido}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
